import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [devLink, setDevLink] = useState('');
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      if (data.reset_link) {
        setDevLink(data.reset_link);
      }
      toast.success('If your email exists, a reset link has been sent.');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Button
          data-testid="back-to-login-button"
          variant="ghost"
          onClick={() => navigate('/login')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to login
        </Button>

        <Card className="p-8 border border-stone-200">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-red-50 rounded-full">
                <Mail className="w-8 h-8 text-[#C53030]" />
              </div>
            </div>
            <h2 className="text-xl sm:text-2xl text-[#1C1917] mb-2">Reset your password</h2>
            <p className="text-sm text-stone-600">
              Enter the email associated with your account and we'll send you a reset link.
            </p>
          </div>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1"
                />
              </div>

              <Button
                data-testid="forgot-submit-button"
                type="submit"
                disabled={loading}
                className="w-full bg-[#C53030] hover:bg-[#9B2C2C] text-white rounded-md"
              >
                {loading ? 'Sending...' : 'Send reset link'}
              </Button>
            </form>
          ) : (
            <div className="text-center">
              <p className="text-base text-stone-800 leading-relaxed mb-4">
                If an account exists for <span className="font-semibold">{email}</span>, we've sent a password reset link.
              </p>
              {devLink && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md text-left text-xs mb-4">
                  <p className="font-semibold text-yellow-900 mb-2">Development Mode Link:</p>
                  <a
                    href={devLink}
                    data-testid="dev-reset-link"
                    className="text-[#C53030] hover:underline break-all"
                  >
                    {devLink}
                  </a>
                  <p className="mt-2 text-yellow-800">Configure Resend/SendGrid to send real emails.</p>
                </div>
              )}
              <Button
                data-testid="back-to-login-after-submit"
                variant="outline"
                onClick={() => navigate('/login')}
                className="border border-stone-200"
              >
                Back to login
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
